import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/ui/components/Button";
import { Icon } from "@/ui/icons/Icon";
import { useKyc } from "../../hooks/useKyc";
import { useKycSignalR } from "../../hooks/useKycSignalR";
import { type ProofOfAddressDocumentType } from "../../types/kyc";
import { DocumentInReview } from "@/components/DocumentInReview";
import { useLoanOffer } from "@/features/offer/hooks/useLoanOffer";
import { hasAvailableOffer } from "@/lib/offerUtils";
import { routes } from "@/routes/routes";
import styles from "./ProofOfAddressPage.module.scss";

const VERIFICATION_POLL_MS = 15000;

function ProofOfAddressPage() {
  useKycSignalR();
  const navigate = useNavigate();
  const { uploadProofOfAddress, isProofUploading, kycState, getKycState } =
    useKyc(true);
  const { loanOfferState, isLoading: isLoanOfferLoading } = useLoanOffer(true);
  const hasFinalOffer = hasAvailableOffer(loanOfferState?.creditOffers);
  // With a final offer the proof is what stands before the contract: there is
  // no later to come back to.
  const canPostpone = !isLoanOfferLoading && !hasFinalOffer;
  const isPendingVerification = !!kycState?.isProofPendingVerification;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedType, setSelectedType] =
    useState<ProofOfAddressDocumentType | null>(null);

  const certificateInputRef = useRef<HTMLInputElement>(null);
  const invoiceInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      kycState &&
      !kycState.isProofPendingVerification &&
      !kycState.requiresProofOfAddress
    ) {
      navigate(hasFinalOffer ? routes.application.base : routes.kyc.base, {
        replace: true,
      });
    }
  }, [kycState]);

  useEffect(() => {
    if (!isPendingVerification) return;

    const intervalId = setInterval(getKycState, VERIFICATION_POLL_MS);
    return () => clearInterval(intervalId);
  }, [isPendingVerification]);

  const handleFileChange =
    (type: ProofOfAddressDocumentType) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        setSelectedType(type);
      }
      e.target.value = "";
    };

  const handleContinue = async () => {
    if (!selectedFile || !selectedType) return;

    // A proof under review keeps the user here, on the review screen.
    const updatedState = await uploadProofOfAddress({
      file: selectedFile,
      documentType: selectedType,
    });

    if (!updatedState.isProofPendingVerification) {
      navigate(routes.wizard.base);
    }
  };

  const isValid = !!selectedFile;

  if (isPendingVerification) {
    return (
      <DocumentInReview description="OceanCredit analizează informațiile oferite și te va contacta în caz că e nevoie de mai multe detalii." />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.emoji} role="img" aria-label="dovada adresei">
            🧾
          </span>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Dovada adresei</h1>
            <p className={styles.subtitle}>
              Te rugăm să încarci un certificat sau o factură ca dovadă a
              adresei.
            </p>
          </div>
        </div>

        <div className={styles.cards}>
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.cardTitle}>Atestare domiciliu/reședință</p>
              <p className={styles.cardDescription}>
                Acum poți cere online eliberarea în maxim 30 de minute a
                certificatului de atestare a domiciliului și/sau reședinței pe{" "}
                <a
                  className={styles.link}
                  href="https://hub.mai.gov.ro/serviciu/view?id=29"
                  target="_blank"
                  rel="noreferrer"
                >
                  hub.mai.gov.ro
                </a>
              </p>
            </div>

            <input
              ref={certificateInputRef}
              type="file"
              accept="image/*,application/pdf"
              className={styles.fileInput}
              onChange={handleFileChange("certificate")}
            />
            <Button
              trackingId="upload_proof_of_address_certificate"
              variant="tonal"
              text={
                selectedType === "certificate" && selectedFile
                  ? selectedFile.name
                  : "Încarcă certificat"
              }
              leftIcon={<Icon name="file-upload" size={16} />}
              className={styles.uploadButton}
              onClick={() => certificateInputRef.current?.click()}
            />
          </div>

          <div className={styles.divider}>
            <span>SAU</span>
          </div>

          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.cardTitle}>Factură</p>
              <p className={styles.cardDescription}>
                Te rugăm să încarci o factură în care să apară numele și adresa
                ta.
              </p>
            </div>

            <input
              ref={invoiceInputRef}
              type="file"
              accept="image/*,application/pdf"
              className={styles.fileInput}
              onChange={handleFileChange("invoice")}
            />
            <Button
              trackingId="upload_proof_of_address_invoice"
              variant="tonal"
              text={
                selectedType === "invoice" && selectedFile
                  ? selectedFile.name
                  : "Încarcă o factură"
              }
              leftIcon={<Icon name="file-upload" size={16} />}
              className={styles.uploadButton}
              onClick={() => invoiceInputRef.current?.click()}
            />
          </div>
        </div>
      </div>

      <div className={styles.bottomSection}>
        <div className={styles.buttons}>
          {canPostpone && (
            <Button
              trackingId="proof_of_address_later"
              variant="tonal"
              text="Mai târziu"
              className={styles.laterButton}
              onClick={() => navigate(routes.wizard.base)}
            />
          )}
          <Button
            trackingId="submit_proof_of_address"
            variant="filled"
            text="Continuă"
            disabled={!isValid || isProofUploading}
            loading={isProofUploading}
            rightIcon={<Icon name="arrow-right" size={16} />}
            className={styles.continueButton}
            onClick={handleContinue}
          />
        </div>
      </div>
    </div>
  );
}

export default ProofOfAddressPage;
